import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Mail, Phone, Calendar, Building, LogOut, ClipboardList } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPartnerships = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    fetchInquiries(token);
  }, [navigate]);

  const fetchInquiries = async (token) => {
    try {
      const response = await axios.get(`${API}/partnerships`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInquiries(response.data.inquiries || []);
      setLoading(false);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
      console.error('Error fetching inquiries:', error);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <p className="text-xl text-gray-600">Loading inquiries...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="partnerships-page-title">
                Partnership <span className="text-[#E6B933]">Inquiries</span>
              </h1>
              <p className="text-lg text-gray-600">
                {inquiries.length} inquir{inquiries.length !== 1 ? 'ies' : 'y'} received
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/admin/volunteers">
                <Button variant="outline" className="border-[#E6B933] text-[#E6B933] hover:bg-[#E6B933]/10" data-testid="nav-volunteers">
                  <ClipboardList className="w-4 h-4 mr-2" /> Volunteers
                </Button>
              </Link>
              <Button variant="outline" onClick={handleLogout} className="text-red-500 border-red-300 hover:bg-red-50" data-testid="admin-logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </div>
          </div>

          <div className="space-y-4">
            {inquiries.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-gray-600">No inquiries yet.</p>
              </Card>
            ) : (
              inquiries.map((inquiry, index) => (
                <Card key={inquiry._id || index} className="p-6 hover:shadow-lg transition-all duration-300">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center space-x-2 mb-2">
                        <Building className="text-[#E6B933]" size={24} />
                        <h3 className="text-xl font-bold text-gray-900">{inquiry.organization}</h3>
                      </div>
                      <p className="text-gray-700 font-semibold">{inquiry.contact_name}</p>
                      <p className="text-[#E6B933]">{inquiry.partnership_type}</p>
                    </div>
                    <Badge className="bg-[#E6B933] text-white">
                      {inquiry.status || 'pending'}
                    </Badge>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Mail size={16} />
                      <a href={`mailto:${inquiry.email}`} className="hover:text-[#E6B933]">
                        {inquiry.email}
                      </a>
                    </div>
                    {inquiry.phone && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Phone size={16} />
                        <a href={`tel:${inquiry.phone}`} className="hover:text-[#E6B933]">
                          {inquiry.phone}
                        </a>
                      </div>
                    )}
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Calendar size={16} />
                      <span>{new Date(inquiry.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {inquiry.message && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Message:</p>
                      <p className="text-gray-600">{inquiry.message}</p>
                    </div>
                  )}
                </Card>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminPartnerships;
